import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  MaterialProduct,
  MaterialRateVersion,
  MaterialSettingsResponse,
  MaterialOrderItem,
  MaterialOrderFilesResponse,
  MaterialOrderPartsResponse,
  MaterialOwnerPostcardsResponse,
  MaterialStuckJobsResponse,
  MaterialOrderPartItem,
} from '../../api/materialApi';

// ----------------------------------------------------------------------
// State Interface
// ----------------------------------------------------------------------

export interface MaterialRatesState {
  items: MaterialRateVersion[];
  total: number;
  limit: number;
  offset: number;
  loading: boolean;
  creating: boolean;
  error: string | null;
}

export interface MaterialState {
  products: {
    items: MaterialProduct[];
    loading: boolean;
    updating: boolean;
    error: string | null;
  };
  rates: {
    byProduct: Record<string, MaterialRatesState>;
  };
  settings: {
    data: MaterialSettingsResponse | null;
    loading: boolean;
    saving: boolean;
    error: string | null;
  };
  orders: {
    items: MaterialOrderItem[];
    total: number;
    limit: number;
    offset: number;
    stuck_after_minutes: number;
    stuck_generating: number;
    loading: boolean;
    error: string | null;
  };
  selectedOrder: {
    data: MaterialOrderItem | null;
    loading: boolean;
    actionLoading: boolean;
    error: string | null;
  };
  // Phase 11c: Order files & parts
  selectedOrderFiles: {
    data: MaterialOrderFilesResponse | null;
    loading: boolean;
    error: string | null;
  };
  selectedOrderParts: {
    data: MaterialOrderPartsResponse | null;
    loading: boolean;
    error: string | null;
  };
  // Phase 11c: Postcards Monitor
  postcards: {
    data: MaterialOwnerPostcardsResponse | null;
    loading: boolean;
    error: string | null;
  };
  // Phase 11c: Stuck Jobs Monitor
  stuckJobs: {
    data: MaterialStuckJobsResponse | null;
    loading: boolean;
    error: string | null;
  };
}

const initialState: MaterialState = {
  products: {
    items: [],
    loading: false,
    updating: false,
    error: null,
  },
  rates: {
    byProduct: {},
  },
  settings: {
    data: null,
    loading: false,
    saving: false,
    error: null,
  },
  orders: {
    items: [],
    total: 0,
    limit: 50,
    offset: 0,
    stuck_after_minutes: 30,
    stuck_generating: 0,
    loading: false,
    error: null,
  },
  selectedOrder: {
    data: null,
    loading: false,
    actionLoading: false,
    error: null,
  },
  selectedOrderFiles: {
    data: null,
    loading: false,
    error: null,
  },
  selectedOrderParts: {
    data: null,
    loading: false,
    error: null,
  },
  postcards: {
    data: null,
    loading: false,
    error: null,
  },
  stuckJobs: {
    data: null,
    loading: false,
    error: null,
  },
};

// ----------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------

export const materialSlice = createSlice({
  name: 'material',
  initialState,
  reducers: {
    // Products
    setMaterialProductsLoading(state, action: PayloadAction<boolean>) {
      state.products.loading = action.payload;
      if (action.payload) state.products.error = null;
    },
    setMaterialProductsUpdating(state, action: PayloadAction<boolean>) {
      state.products.updating = action.payload;
      if (action.payload) state.products.error = null;
    },
    setMaterialProductsError(state, action: PayloadAction<string | null>) {
      state.products.error = action.payload;
      state.products.loading = false;
      state.products.updating = false;
    },
    setMaterialProducts(state, action: PayloadAction<MaterialProduct[]>) {
      state.products.items = action.payload;
      state.products.loading = false;
      state.products.updating = false;
      state.products.error = null;
    },
    updateMaterialProductInList(state, action: PayloadAction<MaterialProduct>) {
      const idx = state.products.items.findIndex((p) => p.code === action.payload.code);
      if (idx !== -1) {
        state.products.items[idx] = action.payload;
      }
      state.products.updating = false;
      state.products.error = null;
    },

    // Rates
    setMaterialRatesLoading(state, action: PayloadAction<{ code: string; loading: boolean }>) {
      const { code, loading } = action.payload;
      if (!state.rates.byProduct[code]) {
        state.rates.byProduct[code] = {
          items: [],
          total: 0,
          limit: 50,
          offset: 0,
          loading,
          creating: false,
          error: null,
        };
      } else {
        state.rates.byProduct[code].loading = loading;
        if (loading) state.rates.byProduct[code].error = null;
      }
    },
    setMaterialRatesCreating(state, action: PayloadAction<{ code: string; creating: boolean }>) {
      const { code, creating } = action.payload;
      if (!state.rates.byProduct[code]) {
        state.rates.byProduct[code] = {
          items: [],
          total: 0,
          limit: 50,
          offset: 0,
          loading: false,
          creating,
          error: null,
        };
      } else {
        state.rates.byProduct[code].creating = creating;
        if (creating) state.rates.byProduct[code].error = null;
      }
    },
    setMaterialRatesError(state, action: PayloadAction<{ code: string; error: string | null }>) {
      const { code, error } = action.payload;
      if (!state.rates.byProduct[code]) {
        state.rates.byProduct[code] = {
          items: [],
          total: 0,
          limit: 50,
          offset: 0,
          loading: false,
          creating: false,
          error,
        };
      } else {
        state.rates.byProduct[code].error = error;
        state.rates.byProduct[code].loading = false;
        state.rates.byProduct[code].creating = false;
      }
    },
    setMaterialRatesData(
      state,
      action: PayloadAction<{
        code: string;
        items: MaterialRateVersion[];
        total: number;
        limit: number;
        offset: number;
      }>
    ) {
      const { code, items, total, limit, offset } = action.payload;
      state.rates.byProduct[code] = {
        items,
        total,
        limit,
        offset,
        loading: false,
        creating: false,
        error: null,
      };
      // Keep current_rate in the products list in step. Migration 0030: an old per-voter version
      // is never current, so a slip product with only such versions has no current rate.
      const currentRate = items.find((r) => r.is_current) || null;
      const productIdx = state.products.items.findIndex((p) => p.code === code);
      if (productIdx !== -1 && (currentRate || offset === 0)) {
        state.products.items[productIdx].current_rate = currentRate;
      }
    },

    // Settings
    setMaterialSettingsLoading(state, action: PayloadAction<boolean>) {
      state.settings.loading = action.payload;
      if (action.payload) state.settings.error = null;
    },
    setMaterialSettingsSaving(state, action: PayloadAction<boolean>) {
      state.settings.saving = action.payload;
      if (action.payload) state.settings.error = null;
    },
    setMaterialSettingsError(state, action: PayloadAction<string | null>) {
      state.settings.error = action.payload;
      state.settings.loading = false;
      state.settings.saving = false;
    },
    setMaterialSettingsData(state, action: PayloadAction<MaterialSettingsResponse>) {
      state.settings.data = action.payload;
      state.settings.loading = false;
      state.settings.saving = false;
      state.settings.error = null;
    },

    // Orders List
    setMaterialOrdersLoading(state, action: PayloadAction<boolean>) {
      state.orders.loading = action.payload;
      if (action.payload) state.orders.error = null;
    },
    setMaterialOrdersError(state, action: PayloadAction<string | null>) {
      state.orders.error = action.payload;
      state.orders.loading = false;
    },
    setMaterialOrdersPage(
      state,
      action: PayloadAction<{
        items: MaterialOrderItem[];
        total: number;
        limit: number;
        offset: number;
        stuck_after_minutes: number;
        stuck_generating: number;
      }>
    ) {
      state.orders.items = action.payload.items;
      state.orders.total = action.payload.total;
      state.orders.limit = action.payload.limit;
      state.orders.offset = action.payload.offset;
      state.orders.stuck_after_minutes = action.payload.stuck_after_minutes;
      state.orders.stuck_generating = action.payload.stuck_generating;
      state.orders.loading = false;
      state.orders.error = null;
    },
    updateMaterialOrderItem(state, action: PayloadAction<MaterialOrderItem>) {
      const idx = state.orders.items.findIndex((o) => o.id === action.payload.id);
      if (idx !== -1) {
        state.orders.items[idx] = action.payload;
      }
      if (state.selectedOrder.data?.id === action.payload.id) {
        state.selectedOrder.data = action.payload;
      }
      state.selectedOrder.actionLoading = false;
    },

    // Selected Order Detail
    setSelectedOrderLoading(state, action: PayloadAction<boolean>) {
      state.selectedOrder.loading = action.payload;
      if (action.payload) state.selectedOrder.error = null;
    },
    setSelectedOrderActionLoading(state, action: PayloadAction<boolean>) {
      state.selectedOrder.actionLoading = action.payload;
      if (action.payload) state.selectedOrder.error = null;
    },
    setSelectedOrderError(state, action: PayloadAction<string | null>) {
      state.selectedOrder.error = action.payload;
      state.selectedOrder.loading = false;
      state.selectedOrder.actionLoading = false;
    },
    setSelectedOrderData(state, action: PayloadAction<MaterialOrderItem | null>) {
      state.selectedOrder.data = action.payload;
      state.selectedOrder.loading = false;
      state.selectedOrder.error = null;
    },

    // Phase 11c: Selected Order Files
    setSelectedOrderFilesLoading(state, action: PayloadAction<boolean>) {
      state.selectedOrderFiles.loading = action.payload;
      if (action.payload) state.selectedOrderFiles.error = null;
    },
    setSelectedOrderFilesError(state, action: PayloadAction<string | null>) {
      state.selectedOrderFiles.error = action.payload;
      state.selectedOrderFiles.loading = false;
    },
    setSelectedOrderFilesData(state, action: PayloadAction<MaterialOrderFilesResponse | null>) {
      state.selectedOrderFiles.data = action.payload;
      state.selectedOrderFiles.loading = false;
      state.selectedOrderFiles.error = null;
    },

    // Phase 11c: Selected Order Parts
    setSelectedOrderPartsLoading(state, action: PayloadAction<boolean>) {
      state.selectedOrderParts.loading = action.payload;
      if (action.payload) state.selectedOrderParts.error = null;
    },
    setSelectedOrderPartsError(state, action: PayloadAction<string | null>) {
      state.selectedOrderParts.error = action.payload;
      state.selectedOrderParts.loading = false;
    },
    setSelectedOrderPartsData(state, action: PayloadAction<MaterialOrderPartsResponse | null>) {
      state.selectedOrderParts.data = action.payload;
      state.selectedOrderParts.loading = false;
      state.selectedOrderParts.error = null;
    },
    updatePartInList(state, action: PayloadAction<MaterialOrderPartItem>) {
      if (state.selectedOrderParts.data?.items) {
        const idx = state.selectedOrderParts.data.items.findIndex(
          (p) => p.part_index === action.payload.part_index
        );
        if (idx !== -1) {
          state.selectedOrderParts.data.items[idx] = action.payload;
        }
      }
    },

    // Phase 11c: Postcards Monitor
    setPostcardsLoading(state, action: PayloadAction<boolean>) {
      state.postcards.loading = action.payload;
      if (action.payload) state.postcards.error = null;
    },
    setPostcardsError(state, action: PayloadAction<string | null>) {
      state.postcards.error = action.payload;
      state.postcards.loading = false;
    },
    setPostcardsData(state, action: PayloadAction<MaterialOwnerPostcardsResponse | null>) {
      state.postcards.data = action.payload;
      state.postcards.loading = false;
      state.postcards.error = null;
    },

    // Phase 11c: Stuck Jobs Monitor
    setStuckJobsLoading(state, action: PayloadAction<boolean>) {
      state.stuckJobs.loading = action.payload;
      if (action.payload) state.stuckJobs.error = null;
    },
    setStuckJobsError(state, action: PayloadAction<string | null>) {
      state.stuckJobs.error = action.payload;
      state.stuckJobs.loading = false;
    },
    setStuckJobsData(state, action: PayloadAction<MaterialStuckJobsResponse | null>) {
      state.stuckJobs.data = action.payload;
      state.stuckJobs.loading = false;
      state.stuckJobs.error = null;
    },
  },
});

export const {
  setMaterialProductsLoading,
  setMaterialProductsUpdating,
  setMaterialProductsError,
  setMaterialProducts,
  updateMaterialProductInList,
  setMaterialRatesLoading,
  setMaterialRatesCreating,
  setMaterialRatesError,
  setMaterialRatesData,
  setMaterialSettingsLoading,
  setMaterialSettingsSaving,
  setMaterialSettingsError,
  setMaterialSettingsData,
  setMaterialOrdersLoading,
  setMaterialOrdersError,
  setMaterialOrdersPage,
  updateMaterialOrderItem,
  setSelectedOrderLoading,
  setSelectedOrderActionLoading,
  setSelectedOrderError,
  setSelectedOrderData,
  setSelectedOrderFilesLoading,
  setSelectedOrderFilesError,
  setSelectedOrderFilesData,
  setSelectedOrderPartsLoading,
  setSelectedOrderPartsError,
  setSelectedOrderPartsData,
  updatePartInList,
  setPostcardsLoading,
  setPostcardsError,
  setPostcardsData,
  setStuckJobsLoading,
  setStuckJobsError,
  setStuckJobsData,
} = materialSlice.actions;

export default materialSlice.reducer;
